import React from 'react';
import {
  Button as RACButton,
  type ButtonProps as RACButtonProps,
  ToggleButton as RACToggleButton,
  type ToggleButtonProps as RACToggleButtonProps,
  composeRenderProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { SpinnerIcon } from './icons';
import { type AsChildProps, Slot } from './slot';
import { focusVisibleOutlineStyle } from './utils';

type Color = 'default' | 'accent' | 'success' | 'destructive' | 'warning' | 'muted' | 'link';

type Size = 'sm' | 'lg';

type Variant = 'solid' | 'outline' | 'ghost' | 'plain' | 'unstyle';

export type BasicButtonProps = {
  color?: Color;
  size?: Size;
  isCustomPending?: boolean;
  isIconOnly?: boolean;
  pendingLabel?: string;
  variant?: Variant;
};

export type ButtonProps = AsChildProps<RACButtonProps> & BasicButtonProps;

export type ButtonWithoutAsChildProps = RACButtonProps & BasicButtonProps;

const buttonVariants = {
  base: [
    'group relative inline-flex justify-center items-center whitespace-nowrap rounded-sm outline-hidden',
    'disabled:opacity-50',
    'data-pending:opacity-75',
  ],
  solid: [
    'border border-[var(--btn-bg)] dark:border-none dark:[--border-width:0px]',
    'bg-[var(--btn-bg)] hover:bg-[var(--btn-bg-hover)] pressed:bg-[var(--btn-bg-hover)]',
    'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.1)]',
    'text-[var(--btn-foreground)]',
  ],
  outline: [
    'border border-[var(--btn-border)] border-b-[var(--btn-border-b)]',
    'hover:bg-zinc-50 pressed:bg-zinc-50 dark:hover:bg-zinc-800 dark:pressed:bg-zinc-800',
    'shadow-xs',
    'text-[var(--btn-foreground)]',
  ],
  ghost: [
    '[--border-width:0px]',
    'hover:bg-zinc-50 pressed:bg-zinc-50 dark:hover:bg-zinc-800 dark:pressed:bg-zinc-800',
    'shadow-xs',
    'text-[var(--btn-foreground)]',
  ],
  plain: [
    '[--border-width:0px]',
    'hover:bg-zinc-50 dark:hover:bg-zinc-800',
    'text-[var(--btn-foreground)]',
  ],
};

const buttonSizes = {
  sm: {
    button: [
      'h-8 sm:h-7 px-2 gap-x-1 text-sm/6 sm:text-xs/6 rounded-sm font-medium',
      // svg
      '[&_svg:not([class*=size-])]:size-3',
    ],
    iconOnly: [
      'size-8 sm:size-7 rounded-sm',
      //svg
      '[&_svg:not([class*=size-])]:size-5',
      'sm:[&_svg:not([class*=size-])]:size-4',
    ],
  },
  md: {
    // H: 44px, sm:36px
    button: [
      'gap-x-2 text-base/6 sm:text-sm/6 font-semibold',
      'px-3',
      'py-[calc(--spacing(2.5)-var(--border-width,1px))]',
      'sm:py-[calc(--spacing(2)-var(--border-width,1px))]',

      // svg
      '[&_svg:not([class*=size-])]:size-5',
      'sm:[&_svg:not([class*=size-])]:size-4',
    ],
    iconOnly: [
      'p-[calc(--spacing(2.5)-var(--border-width,1px))]',
      'sm:p-[calc(--spacing(2)-var(--border-width,1px))]',

      // svg
      // 20+2x2=24px
      '[&_svg:not([class*=size-])]:size-5',
      '[&_svg]:m-0.5',

      // 16+4x2=24px
      'sm:[&_svg:not([class*=size-])]:size-4',
      'sm:[&_svg]:m-1',
    ],
  },
  lg: {
    button: [
      'gap-x-2 text-base/6 sm:text-sm/6 font-semibold',
      'px-4',
      'py-[calc(--spacing(2.5)-var(--border-width,1px))]',
      // svg
      '[&_svg:not([class*=size-])]:size-5',
    ],
    iconOnly: [
      'p-[calc(--spacing(2.5)-var(--border-width,1px))]',
      // svg
      '[&_svg:not([class*=size-])]:size-5',
      '[&_svg]:m-0.5',
    ],
  },
};

function buttonStyle({ size, color, isIconOnly, variant = 'solid' }: BasicButtonProps) {
  if (variant === 'unstyle') {
    return 'relative outline-hidden rounded-sm';
  }

  const buttonSize = size ?? 'md';
  const buttonType = isIconOnly ? 'iconOnly' : 'button';

  const buttonBackground = {
    default: [
      '[--btn-bg:var(--solid)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--solid)_90%,var(--background))]',
      '[--btn-foreground:var(--solid-foreground)]',
    ],
    accent: [
      '[--btn-bg:var(--accent)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--accent)_90%,var(--background))]',
      '[--btn-foreground:var(--accent-foreground)]',
    ],
    destructive: [
      '[--btn-bg:var(--destructive)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--destructive)_90%,var(--background))]',
      '[--btn-foreground:var(--destructive-foreground)]',
    ],
    success: [
      '[--btn-bg:var(--success)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--success)_90%,var(--background))]',
      '[--btn-foreground:var(--success-foreground)]',
    ],
    warning: [
      '[--btn-bg:var(--warning)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--warning)_90%,var(--background))]',
      '[--btn-foreground:var(--warning-foreground)]',
    ],
    muted: [
      '[--btn-bg:var(--muted)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--muted)_90%,var(--background))]',
      '[--btn-foreground:var(--muted-foreground)]',
    ],
    link: [
      '[--btn-bg:var(--link)]',
      '[--btn-bg-hover:color-mix(in_srgb,var(--link)_90%,var(--background))]',
      '[--btn-foreground:var(--link-foreground)]',
    ],
  };

  const buttonGhost = {
    default: ['[--btn-foreground:var(--solid)]'],
    accent: ['[--btn-foreground:var(--accent)]'],
    destructive: ['[--btn-foreground:var(--destructive)]'],
    success: ['[--btn-foreground:var(--success)]'],
    warning: ['[--btn-foreground:var(--warning)]'],
    muted: ['[--btn-foreground:var(--muted)]'],
    link: ['[--btn-foreground:var(--link)]'],
  };

  const buttonOutline = {
    default: [
      '[--btn-border:color-mix(in_srgb,var(--solid)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--solid)_40%,var(--background))]',
    ],
    accent: [
      '[--btn-border:color-mix(in_srgb,var(--accent)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--accent)_40%,var(--background))]',
    ],
    destructive: [
      '[--btn-border:color-mix(in_srgb,var(--destructive)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--destructive)_40%,var(--background))]',
    ],
    success: [
      '[--btn-border:color-mix(in_srgb,var(--success)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--success)_40%,var(--background))]',
    ],
    warning: [
      '[--btn-border:color-mix(in_srgb,var(--warning)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--warning)_40%,var(--background))]',
    ],
    muted: [
      '[--btn-border:color-mix(in_srgb,var(--muted)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--muted)_40%,var(--background))]',
    ],
    link: [
      '[--btn-border:color-mix(in_srgb,var(--link)_25%,var(--background))]',
      '[--btn-border-b:color-mix(in_srgb,var(--link)_40%,var(--background))]',
    ],
  };

  const buttonColor = {
    default: '[--btn-color:var(--solid)]',
    foreground: '[--btn-foreground:var(--foreground)]',
    accent: '[--btn-color:var(--accent)]',
    destructive: '[--btn-color:var(--destructive)]',
    success: '[--btn-color:var(--success)]',
    warning: '[--btn-color:var(--warning)]',
    muted: '[--btn-color:var(--muted)]',
    link: '[--btn-color:var(--link)]',
  };

  return [
    buttonVariants.base,
    buttonVariants[variant],
    variant == 'solid' ? [buttonBackground[color ?? 'default']] : [buttonColor[color ?? 'default']],
    variant == 'outline' || variant == 'ghost' ? [buttonGhost[color ?? 'default']] : '',
    variant == 'outline' ? [buttonOutline[color ?? 'default']] : '',
    buttonSizes[buttonSize]?.[buttonType],
  ];
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  if (props.asChild) {
    return <Slot className={twMerge(buttonStyle(props))}>{props.children}</Slot>;
  }

  const {
    asChild,
    children,
    isCustomPending,
    pendingLabel,
    size,
    color,
    variant,
    isIconOnly,
    ...buttonProps
  } = props;

  return (
    <>
      <RACButton
        {...buttonProps}
        ref={ref}
        data-variant={variant ?? 'solid'}
        className={composeRenderProps(props.className, (className, { isPending }) => {
          return twMerge(
            buttonStyle({ size, color, isIconOnly, variant }),
            focusVisibleOutlineStyle,
            isPending && !isCustomPending && 'text-transparent',
            className,
          );
        })}
      >
        {(renderProps) => {
          return (
            <>
              {renderProps.isPending && !isCustomPending && (
                <SpinnerIcon
                  className={twMerge(
                    'absolute flex h-full items-center justify-center',
                    renderProps.isPending ? (isCustomPending ? 'sr-only' : 'flex') : 'hidden',
                    (!variant || variant === 'solid') && 'text-zinc-300',
                  )}
                />
              )}

              <span className="contents" {...(renderProps.isPending && { 'aria-hidden': true })}>
                {typeof children === 'function' ? children(renderProps) : children}
              </span>
              <span
                className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] [@media(pointer:fine)]:hidden"
                aria-hidden="true"
              ></span>
            </>
          );
        }}
      </RACButton>
    </>
  );
});

export type ToggleButtonProps = RACToggleButtonProps & BasicButtonProps;

export function ToggleButton(props: ToggleButtonProps) {
  return (
    <RACToggleButton
      {...props}
      className={composeRenderProps(props.className, (className) =>
        twMerge(buttonStyle(props), focusVisibleOutlineStyle, className),
      )}
    />
  );
}

export function ButtonGroup({
  className,
  blend,
  orientation = 'horizontal',
  ...props
}: JSX.IntrinsicElements['div'] & {
  blend?: boolean;
  orientation?: 'horizontal' | 'vertical';
}) {
  return (
    <div
      {...props}
      data-ui="button-group"
      className={twMerge(
        'group flex w-max items-center',

        orientation === 'horizontal'
          ? [
              '[&>button:first-of-type]:rounded-e-none',
              '[&>button:last-of-type]:rounded-s-none',
              '[&>button:not(:last-of-type)]:border-e-0',
              blend && 'shadow-xs [&>button:not(:first-of-type)]:border-s-0 [&>button]:shadow-none',
            ]
          : [
              'flex-col',
              '[&>button:first-of-type]:rounded-b-none',
              '[&>button:last-of-type]:rounded-t-none',
              '[&>button:not(:last-of-type)]:border-b-0',

              blend && 'shadow-xs [&>button:not(:first-of-type)]:border-t-0 [&>button]:shadow-none',
            ],

        '[&>button:not(:first-of-type):not(:last-of-type)]:rounded-none',

        // Add border to solid button which has not border in dark mode
        'dark:[&>button[data-variant=solid]]:border-solid',
        'dark:[&>button[data-variant=solid]]:[--border-width:1px]',
        '[&>button[data-variant=solid]:not(:first-of-type)]:border-s-black/15',
        className,
      )}
    />
  );
}
